import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
    pushSupported,
    subscribeUser,
    getActiveSubscription,
    refreshAppBadge,
} from "@/lib/push";

/**
 * After login, prompts the user once to enable push alarms. Hidden if:
 * - browser doesn't support push, or
 * - the user already has a subscription, or
 * - they've dismissed it (localStorage key "push_optin_dismissed").
 *
 * Apple iOS Safari requires the app to be installed on the home screen
 * first; we surface that as a hint there.
 */
export default function PushOptinPrompt() {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const [isIos, setIsIos] = useState(false);
    const [standalone, setStandalone] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (!pushSupported()) return;

        const dismissed = localStorage.getItem("push_optin_dismissed");
        const denied = Notification.permission === "denied";
        const ua = window.navigator.userAgent || "";
        const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const installed = window.matchMedia("(display-mode: standalone)").matches
            || window.navigator.standalone === true;
        setIsIos(ios);
        setStandalone(installed);

        if (dismissed || denied) return;
        if (ios && !installed) {
            // On iOS Safari outside the home-screen app push won't work.
            // We still keep silent — the PWA install prompt handles that.
            return;
        }

        (async () => {
            const sub = await getActiveSubscription();
            if (!sub) {
                setTimeout(() => setVisible(true), 1500);
            } else {
                refreshAppBadge();
            }
        })();
    }, [user]);

    const dismiss = () => {
        localStorage.setItem("push_optin_dismissed", String(Date.now()));
        setVisible(false);
    };

    const activate = async () => {
        setBusy(true);
        try {
            const res = await subscribeUser();
            if (res.ok) {
                toast.success("Alarmes activées 🔔");
                setVisible(false);
                refreshAppBadge();
            } else if (res.reason === "denied") {
                toast.error("Permission refusée. Active les notifications dans les réglages.");
                dismiss();
            } else if (res.reason === "vapid_not_configured") {
                toast.error("Push non configuré côté serveur");
                dismiss();
            } else {
                toast.error("Impossible d'activer les alarmes");
            }
        } catch (e) {
            toast.error("Erreur : " + (e?.message || e));
        }
        setBusy(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="push-optin-banner"
            className="fixed left-3 right-3 bottom-20 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40
                rounded-2xl bg-card border border-border shadow-2xl p-3 flex items-start gap-3
                animate-in slide-in-from-bottom-4 duration-300"
        >
            <div className="w-10 h-10 rounded-xl bg-discover-gradient flex items-center justify-center flex-shrink-0">
                <Bell className="text-white" size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-black text-sm leading-tight">
                    Activer les alarmes
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                    Reçois un rappel <b>24h</b> et <b>2h</b> avant tes évènements de l'agenda,
                    et un compteur sur l'icône quand tu as un message non lu.
                </p>
                {isIos && !standalone && (
                    <p className="text-[10px] text-amber-600 mt-1">
                        Sur iPhone : ajoute d'abord Discoveur à ton écran d'accueil
                        pour que les alarmes fonctionnent.
                    </p>
                )}
                <Button
                    size="sm"
                    onClick={activate}
                    disabled={busy}
                    data-testid="push-optin-activate"
                    className="mt-2 h-8 rounded-full bg-discover-gradient text-white text-xs font-bold px-4"
                >
                    {busy ? "Activation…" : "Activer"}
                </Button>
            </div>
            <button
                onClick={dismiss}
                data-testid="push-optin-dismiss"
                aria-label="Fermer"
                className="text-muted-foreground hover:text-foreground p-1 -mt-1 -mr-1"
            >
                <X size={16} />
            </button>
        </div>
    );
}
